import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'

const PLATFORM_OPTIONS = ['meta', 'google', 'tiktok']
const EMPTY_CLIENT = { name: '', slug: '', industry: '', active: true, platforms: [] }

export default function SettingsClients() {
  const { role } = useAuth()
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)   // null | 'new' | client object
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(EMPTY_CLIENT)
  const [search, setSearch] = useState('')
  const [toast, setToast] = useState(null)

  const isAdmin = role === 'admin'

  useEffect(() => { fetchClients() }, [])

  async function fetchClients() {
    setLoading(true)
    const { data } = await supabase
      .from('clients')
      .select('*, ad_accounts(platform, name, meta_account_id, active)')
      .order('name')
    setClients(data ?? [])
    setLoading(false)
  }

  function openNew() {
    setForm(EMPTY_CLIENT)
    setEditing('new')
  }

  function openEdit(client) {
    const platforms = [...new Set((client.ad_accounts ?? []).map(a => a.platform))]
    setForm({ ...client, platforms })
    setEditing(client)
  }

  function closeModal() { setEditing(null) }

  function handleChange(field, value) {
    setForm(f => ({ ...f, [field]: value }))
  }

  function togglePlatform(p) {
    setForm(f => ({
      ...f,
      platforms: f.platforms.includes(p)
        ? f.platforms.filter(x => x !== p)
        : [...f.platforms, p]
    }))
  }

  function autoSlug(name) {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  }

  async function save() {
    if (!form.name.trim()) return
    setSaving(true)
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug || autoSlug(form.name),
        industry: form.industry || null,
        active: form.active,
      }

      if (editing === 'new') {
        const { data, error } = await supabase.from('clients').insert(payload).select().single()
        if (error) throw error
        showToast('✅ Cliente creato')
      } else {
        const { error } = await supabase.from('clients').update(payload).eq('id', editing.id)
        if (error) throw error
        showToast('✅ Cliente aggiornato')
      }
      await fetchClients()
      closeModal()
    } catch (e) {
      showToast('❌ Errore: ' + e.message)
    }
    setSaving(false)
  }

  async function toggleActive(client) {
    await supabase.from('clients').update({ active: !client.active }).eq('id', client.id)
    await fetchClients()
  }

  function showToast(msg) {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.industry ?? '').toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div style={s.root}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Clienti</h1>
          <p style={s.sub}>{clients.length} clienti configurati</p>
        </div>
        {isAdmin && (
          <button style={s.btnPrimary} onClick={openNew}>+ Nuovo Cliente</button>
        )}
      </div>

      <div style={s.toolbar}>
        <input
          style={s.search}
          placeholder="Cerca cliente..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span style={s.count}>{filtered.length} risultati</span>
      </div>

      {loading ? (
        <div style={s.loading}>Caricamento...</div>
      ) : (
        <div style={s.table}>
          <div style={s.thead}>
            <div style={{...s.th, flex: 2}}>Nome</div>
            <div style={{...s.th, flex: 1}}>Settore</div>
            <div style={{...s.th, flex: 1}}>Piattaforme</div>
            <div style={{...s.th, flex: 1}}>Account</div>
            <div style={{...s.th, width: 80}}>Stato</div>
            {isAdmin && <div style={{...s.th, width: 80}}>Azioni</div>}
          </div>
          {filtered.map(c => {
            const platforms = [...new Set((c.ad_accounts ?? []).map(a => a.platform))]
            const accountCount = (c.ad_accounts ?? []).filter(a => a.active).length
            return (
              <div key={c.id} style={s.row}>
                <div style={{...s.td, flex: 2}}>
                  <div style={s.clientName}>{c.name}</div>
                  <div style={s.clientSlug}>{c.slug}</div>
                </div>
                <div style={{...s.td, flex: 1, color: '#8888a8', fontSize: 12}}>{c.industry ?? '—'}</div>
                <div style={{...s.td, flex: 1}}>
                  <div style={{display: 'flex', gap: 4}}>
                    {platforms.map(p => (
                      <span key={p} style={{...s.pill, ...pillColor(p)}}>{p}</span>
                    ))}
                    {!platforms.length && <span style={{fontSize: 11, color: '#55556a'}}>nessuna</span>}
                  </div>
                </div>
                <div style={{...s.td, flex: 1}}>
                  <span style={{fontSize: 12, color: '#8888a8', fontFamily: 'monospace'}}>{accountCount} attivi</span>
                </div>
                <div style={{...s.td, width: 80}}>
                  <span style={{...s.statusBadge, ...(c.active ? s.statusOn : s.statusOff)}}>
                    {c.active ? 'Attivo' : 'Inattivo'}
                  </span>
                </div>
                {isAdmin && (
                  <div style={{...s.td, width: 80, display: 'flex', gap: 6}}>
                    <button style={s.iconBtn} onClick={() => openEdit(c)} title="Modifica">✎</button>
                    <button style={{...s.iconBtn, color: c.active ? '#f59e0b' : '#22c55e'}}
                      onClick={() => toggleActive(c)}
                      title={c.active ? 'Disattiva' : 'Attiva'}>
                      {c.active ? '⏸' : '▶'}
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* MODAL */}
      {editing && (
        <div style={s.overlay} onClick={e => e.target === e.currentTarget && closeModal()}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>{editing === 'new' ? 'Nuovo Cliente' : 'Modifica Cliente'}</h2>
              <button style={s.closeBtn} onClick={closeModal}>✕</button>
            </div>

            <div style={s.fieldGroup}>
              <label style={s.label}>Nome cliente *</label>
              <input
                style={s.input}
                value={form.name}
                onChange={e => {
                  handleChange('name', e.target.value)
                  if (editing === 'new') handleChange('slug', autoSlug(e.target.value))
                }}
                placeholder="es. Acqua & Sapone"
              />
            </div>

            <div style={s.fieldGroup}>
              <label style={s.label}>Slug (URL)</label>
              <input
                style={s.input}
                value={form.slug}
                onChange={e => handleChange('slug', e.target.value)}
                placeholder="es. acqua-e-sapone"
              />
            </div>

            <div style={s.fieldGroup}>
              <label style={s.label}>Settore</label>
              <input
                style={s.input}
                value={form.industry ?? ''}
                onChange={e => handleChange('industry', e.target.value)}
                placeholder="es. Health & Beauty, Retail, Media..."
              />
            </div>

            <div style={s.fieldGroup}>
              <label style={s.label}>Piattaforme attive</label>
              <div style={{display: 'flex', gap: 8}}>
                {PLATFORM_OPTIONS.map(p => (
                  <button
                    key={p}
                    style={{...s.platformBtn, ...(form.platforms.includes(p) ? platformActive(p) : {})}}
                    onClick={() => togglePlatform(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div style={s.fieldGroup}>
              <label style={s.label}>Stato</label>
              <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                <div
                  style={{...s.toggle, background: form.active ? '#6b5ce7' : '#1e1e28'}}
                  onClick={() => handleChange('active', !form.active)}
                >
                  <div style={{...s.toggleKnob, transform: form.active ? 'translateX(14px)' : 'none'}} />
                </div>
                <span style={{fontSize: 12, color: '#8888a8'}}>{form.active ? 'Attivo' : 'Inattivo'}</span>
              </div>
            </div>

            <div style={s.modalFooter}>
              <button style={s.btnGhost} onClick={closeModal}>Annulla</button>
              <button style={s.btnPrimary} onClick={save} disabled={saving}>
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TOAST */}
      {toast && <div style={s.toast}>{toast}</div>}
    </div>
  )
}

function pillColor(p) {
  if (p === 'meta') return { background: 'rgba(24,119,242,0.2)', color: '#60a5fa' }
  if (p === 'google') return { background: 'rgba(234,67,53,0.2)', color: '#f87171' }
  if (p === 'tiktok') return { background: 'rgba(255,0,80,0.2)', color: '#fb7185' }
  return {}
}

function platformActive(p) {
  if (p === 'meta') return { background: 'rgba(24,119,242,0.3)', color: '#60a5fa', borderColor: '#1877f2' }
  if (p === 'google') return { background: 'rgba(234,67,53,0.3)', color: '#f87171', borderColor: '#ea4335' }
  if (p === 'tiktok') return { background: 'rgba(255,0,80,0.3)', color: '#fb7185', borderColor: '#ff0050' }
  return {}
}

const s = {
  root: { padding: 28, flex: 1, overflowY: 'auto', fontFamily: 'Geist, sans-serif' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 600, color: '#e8e8f0', fontFamily: 'DM Serif Display, serif', margin: 0 },
  sub: { fontSize: 12, color: '#55556a', fontFamily: 'monospace', marginTop: 4 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  search: { background: '#17171f', border: '1px solid rgba(255,255,255,0.07)', color: '#e8e8f0', borderRadius: 8, padding: '8px 14px', fontSize: 13, outline: 'none', width: 280, fontFamily: 'Geist, sans-serif' },
  count: { fontSize: 11, color: '#55556a', fontFamily: 'monospace' },
  loading: { color: '#55556a', fontSize: 13, padding: 40, textAlign: 'center' },
  table: { background: '#111118', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, overflow: 'hidden' },
  thead: { display: 'flex', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  th: { fontSize: 10, color: '#55556a', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'monospace' },
  row: { display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.1s' },
  td: { display: 'flex', alignItems: 'center', flexShrink: 0 },
  clientName: { fontSize: 13, fontWeight: 500, color: '#e8e8f0' },
  clientSlug: { fontSize: 10, color: '#55556a', fontFamily: 'monospace', marginTop: 2 },
  pill: { fontSize: 9, padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace', letterSpacing: '0.3px' },
  statusBadge: { fontSize: 10, padding: '3px 8px', borderRadius: 10, fontFamily: 'monospace' },
  statusOn: { background: 'rgba(34,197,94,0.15)', color: '#22c55e' },
  statusOff: { background: 'rgba(255,255,255,0.05)', color: '#55556a' },
  iconBtn: { background: 'transparent', border: 'none', color: '#55556a', cursor: 'pointer', fontSize: 14, padding: '2px 4px', borderRadius: 4, transition: 'color 0.1s' },
  btnPrimary: { background: '#6b5ce7', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Geist, sans-serif' },
  btnGhost: { background: 'transparent', color: '#8888a8', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 18px', fontSize: 13, cursor: 'pointer', fontFamily: 'Geist, sans-serif' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: '#111118', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, width: 460, padding: 28, display: 'flex', flexDirection: 'column', gap: 16 },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  modalTitle: { fontSize: 18, fontFamily: 'DM Serif Display, serif', color: '#e8e8f0', margin: 0 },
  closeBtn: { background: 'transparent', border: 'none', color: '#55556a', cursor: 'pointer', fontSize: 16 },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 11, color: '#55556a', letterSpacing: '0.3px' },
  input: { background: '#17171f', border: '1px solid rgba(255,255,255,0.07)', color: '#e8e8f0', borderRadius: 8, padding: '9px 14px', fontSize: 13, outline: 'none', fontFamily: 'Geist, sans-serif' },
  platformBtn: { padding: '6px 14px', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, background: '#17171f', color: '#55556a', cursor: 'pointer', fontSize: 12, fontFamily: 'monospace', transition: 'all 0.15s' },
  toggle: { width: 32, height: 18, borderRadius: 10, position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 },
  toggleKnob: { position: 'absolute', top: 2, left: 2, width: 14, height: 14, background: 'white', borderRadius: '50%', transition: 'transform 0.2s' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4 },
  toast: { position: 'fixed', bottom: 24, right: 24, background: '#17171f', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '12px 18px', fontSize: 13, color: '#e8e8f0', zIndex: 200, boxShadow: '0 8px 32px rgba(0,0,0,0.4)' },
}
